import React, { useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Copy, Share2 } from "lucide-react";
import { toast } from "sonner";

const MAP_URL = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

const MapButtons = ({ value, label, field }) => (
  <div className="flex items-center gap-1.5 pt-0.5 flex-wrap">
    <a
      href={MAP_URL(value)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-[10px] font-medium bg-[#e8f0fe] text-[#1a73e8] hover:bg-[#d2e3fc] px-2 py-0.5 rounded-full transition-colors"
      data-testid={`maps-link-${field}`}
    >
      <svg viewBox="0 0 24 24" className="h-2.5 w-2.5 fill-current"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg>
      Google Maps
    </a>
    <a
      href={`https://waze.com/ul?q=${encodeURIComponent(value)}&navigate=yes`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-[10px] font-medium bg-[#e6f7fb] text-[#05c8f7] hover:bg-[#ccf0f9] px-2 py-0.5 rounded-full transition-colors"
      data-testid={`waze-link-${field}`}
    >
      <svg viewBox="0 0 24 24" className="h-2.5 w-2.5 fill-current"><path d="M12 2a9 9 0 0 0-9 9c0 1.5.37 2.91 1.02 4.15L2 22l6.85-2.02A8.96 8.96 0 0 0 12 20a9 9 0 0 0 0-18zm-3 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm6 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm-3 5c-1.86 0-3.41-1.28-3.86-3h7.72c-.45 1.72-2 3-3.86 3z"/></svg>
      Waze
    </a>
    {typeof navigator !== 'undefined' && !!navigator.share && (
      <button
        onClick={async () => {
          try {
            await navigator.share({ title: label, text: value });
          } catch { /* user cancelled */ }
        }}
        className="inline-flex items-center gap-1 text-[10px] font-medium bg-[#fdeaea] text-[#cc0000] hover:bg-[#fbd5d5] px-2 py-0.5 rounded-full transition-colors"
        title="Opens your phone's share sheet — pick the Tesla app to send this address to the car"
        data-testid={`tesla-link-${field}`}
      >
        <Share2 className="h-2.5 w-2.5" />
        Tesla
      </button>
    )}
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          toast.success("Address copied");
        } catch {
          toast.error("Couldn't copy — long-press the address instead");
        }
      }}
      className="inline-flex items-center gap-1 text-[10px] font-medium bg-gray-100 text-[#605e5e] hover:bg-gray-200 px-2 py-0.5 rounded-full transition-colors"
      data-testid={`copy-link-${field}`}
    >
      <Copy className="h-2.5 w-2.5" />
      Copy
    </button>
  </div>
);

const linkifyLine = (line, i) => {
  const sep = line.includes('\u2013') ? '\u2013' : ' - ';
  const parts = line.split(sep);
  if (parts.length >= 3) {
    const address = parts.slice(2).join(sep).trim();
    if (/\d/.test(address)) {
      return (
        <div key={i} className="mb-1.5">
          {parts.slice(0, 2).join(sep)}{sep}
          <a
            href={MAP_URL(address)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#1a73e8] underline decoration-dotted hover:text-[#174ea6]"
          >
            {address}
          </a>
          <MapButtons value={address} label="Address" field={`timeline-${i}`} />
        </div>
      );
    }
  }
  return <div key={i}>{line}</div>;
};

export const EditableField = ({ label, field, multiline, value, editMode, onChange, mapLink, linkifyAddresses }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (editMode && multiline && textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }, [value, editMode, multiline]);

  if (editMode) {
    return (
      <div className="space-y-1">
        <label className="text-[11px] font-semibold text-[#8d8b8b] uppercase tracking-wider">{label}</label>
        {multiline ? (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={e => {
              onChange(field, e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring overflow-hidden resize-none"
            style={{ minHeight: '60px' }}
            data-testid={`sheet-${field}`}
          />
        ) : (
          <Input
            value={value}
            onChange={e => onChange(field, e.target.value)}
            className="text-sm"
            data-testid={`sheet-${field}`}
          />
        )}
      </div>
    );
  }
  if (!value) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-[11px] font-semibold text-[#8d8b8b] uppercase tracking-wider">{label}</p>
      {linkifyAddresses ? (
        <div className="text-sm text-[#1f2937]" data-testid={`linkified-${field}`}>
          {value.split('\n').map((line, i) => linkifyLine(line, i))}
        </div>
      ) : (
        <p className="text-sm text-[#1f2937] whitespace-pre-line">{value}</p>
      )}
      {mapLink && <MapButtons value={value} label={label} field={field} />}
    </div>
  );
};
